/**
 * Inline "otazníček" help affordance (originally CZNA-232, promoted to a
 * shared module primitive in CZNA-380).
 *
 * Renders a small info button next to a form control's label or a section
 * heading; clicking it opens a popover with a translated explanation of what
 * the control/section means. Used across settings/admin forms wherever a
 * control's meaning isn't self-evident from its label alone.
 *
 * --- Why a popover and not a tooltip (CZNA-505) ---
 * Until CZNA-505 this rendered a Stripes `<Tooltip>` — a hover-only, dark,
 * centre-aligned, `min-height: 36px` chip with no `max-width`. That is built
 * for a two-word hint, and the help texts it was carrying are not two words:
 * the ontology browser's coverage explanation is 477 characters, and five more
 * are over 200 (`sourceJson` 310, `subfieldsTable` 246, `propertyDetail` 224,
 * `bindsTable` 219, `traceTable` 215). With no `max-width` the chip stretches
 * into one long line, and being hover-bound it also could not be read at
 * leisure: moving the pointer towards it dismissed it, and text inside it could
 * not be selected or copied. Reported from Kontrola 2026-08-07.
 *
 * `<Popover>` fixes all three at once — it opens on click and closes only
 * deliberately (trigger again, outside click, Esc), so the body can wrap over
 * several lines, be read at length, and be selected.
 *
 * This deliberately changes the shared primitive rather than adding a second,
 * "long" help component: every one of these texts is a sentence or more, so a
 * per-call-site choice between tooltip and popover would just be a trap for
 * whoever writes the next help string. The name `HelpTooltip` is kept so the
 * change stays confined to this file (12 call sites across the module, several
 * of them in files other Kontrola tickets are editing in parallel); "tooltip"
 * here means the affordance, not the Stripes component.
 *
 * The trigger is now a real `<button>` rather than the previous
 * non-interactive `<span role="img">` — unavoidable, since the content has to
 * be opened somehow, and it is what makes the help keyboard-reachable at all
 * (the tooltip version only ever appeared on hover/focus of a non-tabbable
 * element). `IconButton` defaults to `type="button"`, so it stays safe inside
 * the forms these live in. Its accessible name is a generic "Nápověda"; the
 * button always sits immediately after the label or heading it belongs to, so
 * that context is what a screen reader reads around it — the same trade
 * stripes-components' own `InfoPopover` makes, which ships a bare unlabelled
 * info `IconButton`.
 *
 * --- i18n / help-kit convention (see also FormHelp.tsx, PaneHelpButton.tsx) ---
 * Field-level help message ids follow `ui-czna-authorities.<area>.help.<field>`
 * (e.g. `ui-czna-authorities.derivationRules.help.fieldTag`). Form/section-level
 * intro text (rendered via `<FormHelp>`) follows
 * `ui-czna-authorities.<area>.help.intro`. `<area>` is the feature area the
 * form belongs to (e.g. `derivationRules`, `settings.forms.visual`).
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconButton, Popover } from '@folio/stripes/components';
/** Accessible name of every help trigger; see the note on naming above. */
var ARIA_LABEL_ID = 'ui-czna-authorities.help.fieldButton.ariaLabel';
var HelpTooltip = function (_a) {
    var messageId = _a.messageId, content = _a.content, testKey = _a.testKey, _b = _a.idPrefix, idPrefix = _b === void 0 ? 'help' : _b, messageValues = _a.messageValues, ariaLabelProp = _a.ariaLabel;
    var intl = useIntl();
    var id = "".concat(idPrefix, "-").concat(testKey);
    var ariaLabel = ariaLabelProp !== null && ariaLabelProp !== void 0 ? ariaLabelProp : intl.formatMessage({ id: ARIA_LABEL_ID });
    // Without an explicit portal the overlay renders inline and is clipped by the
    // first ancestor with `overflow: hidden` — `.pane` for the settings panes,
    // and the MCL viewport for a "?" that sits in a table toolbar. Same fix, and
    // the same upstream type/runtime mismatch, as `PaneHelpButton`: `portal` is
    // declared as `ReactPortal` but `Popper.js` does `PropTypes.instanceOf(Element)`
    // and hands it straight to `ReactDOM.createPortal`, so it wants the DOM node.
    // See the long comment in PaneHelpButton.tsx for the full account.
    var popperProps = {
        portal: document.getElementById('OverlayContainer'),
    };
    var renderTrigger = function (_a) {
        var toggle = _a.toggle, open = _a.open, ref = _a.ref;
        return (React.createElement("span", { style: {
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: '0.25rem',
                verticalAlign: 'middle',
            } },
            React.createElement(IconButton, { ref: ref, icon: "info", size: "small", iconSize: "small", "aria-label": ariaLabel, 
                // IconButton renders `aria-expanded={false}` as no attribute; the string keeps "closed".
                // Boolean(): Popover starts from its `open` prop, which is undefined here (CZNA-1459).
                ariaExpanded: String(Boolean(open)), "aria-haspopup": "dialog", onClick: toggle, "data-testid": id })));
    };
    return (React.createElement(Popover, { renderTrigger: renderTrigger, popperProps: popperProps },
        React.createElement("div", { "data-testid": "".concat(id, "-content"), style: { maxWidth: '420px', maxHeight: '60vh', overflowY: 'auto' } }, content !== null && content !== void 0 ? content : (messageId ? React.createElement(FormattedMessage, { id: messageId, values: messageValues }) : null))));
};
export default HelpTooltip;
